// Size verdict panel renderers. All DOM writes target [data-size-*] nodes inside
// the widget's shadow root; the panel's data-state drives its CSS (styles.ts).
import type { GenerationResponse } from "./types"
import { recommendationNote, sizeUnknownMessage } from "./copy"
import type { Strings } from "./strings"

export function loading(root: ShadowRoot, s: Strings) {
  panel(root).hidden = false
  panel(root).dataset.state = "loading"
  label(root).textContent = s.sizeCalculating
  value(root).hidden = true
  note(root).textContent = s.sizeAnalyzing
}

export function render(root: ShadowRoot, response: GenerationResponse, s: Strings) {
  const data = response.result_data
  if (response.status !== "succeeded" || !data) {
    return unavailable(root, response.failure_class || "size_unavailable", s)
  }

  if (data.size_status === "recommended" && data.recommended_size) {
    panel(root).hidden = false
    panel(root).dataset.state = "recommended"
    label(root).textContent = s.sizeSuggestedLabel
    value(root).hidden = false
    value(root).textContent = data.recommended_size
    note(root).textContent = recommendationNote(data, s)
    return
  }

  unknown(root, data.size_reason, s)
}

export function unknown(root: ShadowRoot, reason: string | null | undefined, s: Strings) {
  panel(root).hidden = false
  panel(root).dataset.state = "unknown"
  label(root).textContent = s.sizeUnknownLabel
  value(root).hidden = true
  note(root).textContent = sizeUnknownMessage(reason, s)
}

export function unavailable(root: ShadowRoot, error: unknown, s: Strings) {
  panel(root).hidden = false
  panel(root).dataset.state = "unavailable"
  label(root).textContent = s.sizeUnavailableLabel
  value(root).hidden = true
  const code = error instanceof Error ? error.message : String(error)
  note(root).textContent = code === "timeout" ? s.sizeTimeoutNote : s.sizeRetryNote
}

export function hide(root: ShadowRoot) {
  const element = panel(root)
  element.hidden = true
  delete element.dataset.state
  const size = value(root)
  size.hidden = true
  size.textContent = ""
  note(root).textContent = ""
}

const panel = (root: ShadowRoot) => root.querySelector<HTMLElement>("[data-size-panel]")!
const label = (root: ShadowRoot) => root.querySelector<HTMLElement>("[data-size-label]")!
const value = (root: ShadowRoot) => root.querySelector<HTMLElement>("[data-size-value]")!
const note = (root: ShadowRoot) => root.querySelector<HTMLElement>("[data-size-note]")!
